import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authContainer } from '../../di/container';
import { useAuthStore } from '../store/auth.store';
import { extractRoles } from '../../domain/entities/auth.entity';

/**
 * Consome o SSO handoff (vindo de "Minhas plataformas" no user-dashboard): lê o
 * custom token do **fragmento** da URL (`/handoff#ct=...`), troca por uma sessão
 * Firebase e conclui o `POST /auth/sign-in` com o `x-scope-id` deste app.
 *
 * Duas coisas aqui não são estilo:
 *
 * - o token vem no fragmento, que **não** é enviado ao servidor: não entra em
 *   access log, `Referer` nem log de proxy;
 * - o fragmento é limpo com `replaceState` **antes** de qualquer `await`, senão
 *   o token fica no histórico do navegador durante a ida e volta da rede.
 *
 * A identidade sai **só** do token. Nada aqui deve inferir quem é o usuário de
 * outra fonte — é o que mantém a página correta se um dia o handoff mintar para
 * um uid diferente do caller (impersonação).
 */
export function HandoffPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const customToken = params.get('ct');

    window.history.replaceState(null, '', window.location.pathname);

    if (!customToken) {
      void navigate('/sign-in', { replace: true });
      return;
    }

    void (async () => {
      try {
        const data =
          await authContainer.repository.signInWithCustomToken(customToken);
        setAuth({
          accessToken: data.accessToken ?? data.tokens?.accessToken ?? '',
          accountId: data.account?.id ?? data.user?.id ?? '',
          email: data.account?.email ?? data.user?.email ?? '',
          roles: extractRoles(data),
        });
        void navigate('/', { replace: true });
      } catch (err) {
        console.error('Falha no handoff SSO', err);
        setError(true);
      }
    })();
  }, [navigate, setAuth]);

  return (
    <div className="bg-(--color-bg-secondary) flex min-h-screen items-center justify-center p-6">
      {error ? (
        <div className="text-center">
          <p className="text-(--color-text-primary) text-sm font-medium">
            Não foi possível concluir o login.
          </p>
          <button
            onClick={() => navigate('/sign-in', { replace: true })}
            className="text-(--color-accent) mt-3 text-sm font-medium hover:underline"
          >
            Ir para o login
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="border-(--color-accent) h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-(--color-text-secondary) text-sm">Entrando…</p>
        </div>
      )}
    </div>
  );
}
